"use client";

import {
  Database,
  Code,
  Layout,
  Server,
  CreditCard,
  CircleUser,
  FileJson,
  Check,
  ArrowRight,
  LogIn,
  Wallet,
  Bot,
  Zap,
  Github,
  BarChart3,
  SquareTerminal,
  Anchor,
} from "lucide-react";
import { TrackedNavButton } from "@/components/ui/tracked-nav-button";
import { useButtonTracking } from "@/lib/useButtonTracking";
import { scrollToPricing } from "@/lib/utils";
import CodeBlock from "../CodeBlock";
import React, { useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";

interface ToolsProps {
  isMobile: boolean;
}

interface Integration {
  name: string;
  logo: string;
  description: string;
}

interface Tool {
  title: string;
  description: string[];
  integrations?: Integration[];
  mockUI?: boolean;
  rightContent?: {
    type: string;
    src: string;
    alt: string;
  };
}

const databaseCode = `model Note {
  id        String    @id @default(uuid())
  userId    String
  title     String
  content   String
  createdAt DateTime @default(now())
  updatedAt DateTime @updatedAt
}

// This minimal Prisma schema focuses solely on the note data the UI

// Other features like chat, account details, and billing are handled
// across in this minimal design.`;

const toolsList: Tool[] = [
  {
    title: "SaaS Launch Kit",
    description: [
      "The NextJS boilerplate with everything you need",
      "Auth, Payment, Backend, Ready to use",
      "Launch your SaaS in days, not weeks",
    ],
    integrations: [
      {
        name: "NextAuth",
        logo: "/tech-stack/next auth.png",
        description: "Google auth, Magic link",
      },
      {
        name: "Stripe",
        logo: "/tech-stack/stripe.png",
        description: "Webhook, Annual/monthly",
      },
      {
        name: "Supabase",
        logo: "/tech-stack/supabase.png",
        description: "Database, Auth, Storage",
      },
      {
        name: "Next.js & Tailwind",
        logo: "/tech-stack/nextjs.png",
        description: "Modern UI framework",
      },
    ],
  },
  {
    title: "MCP Boilerplate",
    description: [
      "Launch & monetise your MCP in hours",
      "Stripe, Auth integrated",
      "Works with Cursor, Claude, and other AI assistants",
    ],
    mockUI: true,
    rightContent: {
      type: "custom",
      src: "",
      alt: "MCP Boilerplate",
    },
  },
  {
    title: "Claude Code Template",
    description: [
      "- Spec driven workflow",
      "- 9+ specialised agents", 
      "- Hooks for auto format, security check",
      "- Powerful commands"
    ],
    rightContent: {
      type: "image",
      src: "/thumbnails/cc_tpl.png",
      alt: "Claude Code Template",
    },
  },
  {
    title: "10xCoder.dev",
    description: [
      "Turn ideas into clear Cursor PRD & effective rules",
      "Project architecture, database schema, UI description, etc.",
    ],
    mockUI: true,
  },
];

export default function Tools({ isMobile }: ToolsProps) {
  const { trackButtonClick } = useButtonTracking();
  const router = useRouter();

  const handleButtonClick = (toolName: string) => {
    // Track the click for analytics
    trackButtonClick(`tool_access_${toolName}`, {
      section: "tools",
      action: "click",
      tool: toolName,
    });

    // Redirect to sign in page instead of scrolling to pricing
    router.push("/auth/signin");
  };

  return (
    <section id="tools" className="bg-black text-white pt-16">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h2 className={`${isMobile ? "text-5xl" : "text-6xl"} font-bold mb-6`}>
          Premium Tools
        </h2>
        <p
          className={`${isMobile ? "text-xl" : "text-2xl"} text-gray-300 mb-8`}
        >
          Launch your AI products in days not weeks with exclusive tools
        </p>

        <div className="space-y-8">
          {toolsList.map((tool, index) => (
            <div
              key={index}
              className="bg-[#111111] border border-gray-800 p-0 overflow-hidden rounded-3xl shadow-lg"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Left side - Text content */}
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <h2 className="text-5xl md:text-6xl font-bold mb-5">
                    {tool.title}
                  </h2>
                  <div className="text-white/70 text-xl mb-6">
                    {tool.description.map((desc, i) => (
                      <p key={i} className="mb-2">
                        {desc}
                      </p>
                    ))}
                  </div>
                  <button
                    onClick={() => handleButtonClick(tool.title)}
                    className="bg-white text-black hover:bg-white/90 py-3 px-8 rounded-full font-medium transition-colors mt-2 w-fit inline-flex items-center group text-base"
                  >
                    <span>
                      {tool.title === "SaaS Launch Kit"
                        ? "Get Template"
                        : tool.title === "MCP Boilerplate"
                          ? "Get Boilerplate"
                          : tool.title === "Claude Code Template"
                            ? "Get Template"
                            : "Get access"}
                    </span>
                    <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                </div>

                {/* Right side - Integration grid or image */}
                <div className="bg-[#111111] relative p-6 md:p-8">
                  {tool.integrations ? (
                    <div className="grid grid-cols-2 gap-4 h-full">
                      {tool.integrations.map((integration, idx) => (
                        <div
                          key={idx}
                          className="flex flex-col items-center bg-[#1a1a1a] rounded-xl p-4 sm:p-6"
                        >
                          <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center relative mb-3">
                            <Image
                              src={integration.logo}
                              alt={integration.name}
                              width={64}
                              height={64}
                              className="object-contain"
                            />
                          </div>
                          <h3 className="font-medium text-white text-center text-base sm:text-lg mb-1">
                            {integration.name}
                          </h3>
                          <p className="text-sm sm:text-base text-gray-400 text-center">
                            {integration.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : tool.title === "MCP Boilerplate" ? (
                    <div className="bg-[#111111] flex flex-col h-full justify-center">
                      <div className="space-y-8">
                        {/* Easy Login */}
                        <div className="flex items-start gap-5">
                          <div className="p-4 bg-purple-600/20 rounded-xl">
                            <LogIn className="h-8 w-8 text-purple-500" />
                          </div>
                          <div>
                            <h3 className="text-xl font-medium text-white mb-1">
                              Easy Login
                            </h3>
                            <p className="text-white/70">
                              OAuth built for quick access
                            </p>
                          </div>
                        </div>

                        {/* Payment */}
                        <div className="flex items-start gap-5">
                          <div className="p-4 bg-blue-600/20 rounded-xl">
                            <Wallet className="h-8 w-8 text-blue-500" />
                          </div>
                          <div>
                            <h3 className="text-xl font-medium text-white mb-1">
                              Payment
                            </h3>
                            <p className="text-white/70">
                              Integrated with Stripe for Usage based / recurring
                              / one-time pricing strategy
                            </p>
                          </div>
                        </div>

                        {/* AI Assistant */}
                        <div className="flex items-start gap-5">
                          <div className="p-4 bg-green-600/20 rounded-xl">
                            <Bot className="h-8 w-8 text-green-500" />
                          </div>
                          <div>
                            <h3 className="text-xl font-medium text-white mb-1">
                              Integrate anywhere
                            </h3>
                            <p className="text-white/70">
                              Works with Cursor, Claude & more
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : tool.mockUI ? (
                    <div className="bg-[#111111] flex flex-col min-h-[400px] md:min-h-[500px]">
                      <div className="flex p-4 justify-between items-center border-b border-white/10">
                        <h4 className="font-medium">Project docs</h4>
                        <button className="bg-white/5 hover:bg-white/10 text-xs py-1 px-3 rounded text-white/70 transition-colors">
                          Iterate with AI
                        </button>
                      </div>

                      <div className="flex-1 grid grid-cols-2 gap-x-6 p-4">
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-sm">
                            <div className="p-1 bg-white/5 rounded">
                              <Layout className="h-4 w-4 text-white/70" />
                            </div>
                            <span className="text-white/80">UI reference</span>
                            <Check className="h-4 w-4 text-white/60 ml-auto" />
                          </div>

                          <div className="flex items-center gap-2 text-sm">
                            <div className="p-1 bg-white/5 rounded">
                              <Code className="h-4 w-4 text-white/70" />
                            </div>
                            <span className="text-white/80">
                              Feature breakdown
                            </span>
                          </div>

                          <div className="flex gap-2 items-start text-sm">
                            <div className="p-1 bg-white/5 rounded mt-0.5">
                              <Server className="h-4 w-4 text-white/70" />
                            </div>
                            <div>
                              <span className="text-white/80 block">Rules</span>
                              <div className="ml-4 mt-2 space-y-3">
                                <div className="flex items-center gap-2">
                                  <div className="p-1 bg-white/5 rounded">
                                    <Layout className="h-3 w-3 text-white/70" />
                                  </div>
                                  <span className="text-white/80 text-xs">
                                    UI doc
                                  </span>
                                  <Check className="h-3 w-3 text-white/60 ml-auto" />
                                </div>

                                <div className="flex items-center gap-2">
                                  <div className="p-1 bg-white/5 rounded">
                                    <Code className="h-3 w-3 text-white/70" />
                                  </div>
                                  <span className="text-white/80 text-xs">
                                    Project structure
                                  </span>
                                  <Check className="h-3 w-3 text-white/60 ml-auto" />
                                </div>

                                <div className="flex items-center gap-2">
                                  <div className="p-1 bg-white/5 rounded">
                                    <Database className="h-3 w-3 text-white/70" />
                                  </div>
                                  <span className="text-white/80 text-xs">
                                    Database & state
                                  </span>
                                  <Check className="h-3 w-3 text-white/60 ml-auto" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="flex flex-col h-full">
                            <div className="mb-3">
                              <h5 className="text-sm font-medium">Database</h5>
                              <p className="text-xs text-white/60">
                                Detailed prompt for following the UI style
                              </p>
                            </div>

                            <div className="bg-[#1a1a1a] flex-1 rounded p-3 text-xs font-mono text-white/80 overflow-hidden whitespace-pre">
                              {`model Note {
  id        String    @id @default(uuid())
  userId    String
  title     String
  content   String
  createdAt DateTime @default(now())
  updatedAt DateTime @updatedAt
}

// This minimal Prisma schema focuses solely on the note data the UI

// Other features like chat, account details, and billing are handled
// across in this minimal design.`}
                            </div>

                            <div className="mt-3 flex justify-end">
                              <TrackedNavButton
                                className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-8 rounded-full font-medium transition-colors mt-2 w-fit inline-flex items-center group text-base"
                                onClick={() => handleButtonClick(tool.title)}
                              >
                                <span>Generate</span>
                                <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                              </TrackedNavButton>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full min-h-[400px] md:min-h-[500px]">
                      <Image
                        src={tool.rightContent?.src || ""}
                        alt={tool.rightContent?.alt || tool.title}
                        width={500}
                        height={300}
                        className="w-full h-full object-cover"
                        priority
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#111111]/90 pointer-events-none"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
